import {LastSeenProduct} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/last_seen_product.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {
  useIsInitialLocation,
  useIsLastNavigationHistoryBack,
} from '@aboutyou/router';
import {useMemo} from 'react';
import {useFrequentlyUpdatingLastSeenProducts} from '../../../features/LastSeenProducts/useLastSeenProductIds';

/**
 * The personalized product injection should not happen on the initial page (direct entry), thus we do not send the last seen products in that case.
 * This is to prevent flickering of products stream due to personalized products being injected after initial SSR render.
 *
 * When navigating back to the page, we want to use the last seen products that were present when the user left the page.
 * This is also to prevent any flickering when navigating back from an ADP.
 */
export const useLastSeenProductsRequestParameter = ({
  uniqueKey,
}: {
  uniqueKey: string;
}) => {
  const allLastSeenProducts = useFrequentlyUpdatingLastSeenProducts();
  const freshLastSeenProducts = useMemo(
    () => allLastSeenProducts.filter(p => !!p.lastSeenAt) as LastSeenProduct[],
    [allLastSeenProducts],
  );

  const [rememberedLastSeenProducts] = useHistoryPageState(
    freshLastSeenProducts,
    {uniqueKey: `maybe_stale_last_seen_products::${uniqueKey}`},
  );

  const isLastNavigationHistoryBack = useIsLastNavigationHistoryBack();

  const isInitialLocation = useIsInitialLocation();
  if (isInitialLocation) {
    return [];
  }

  return isLastNavigationHistoryBack
    ? rememberedLastSeenProducts
    : freshLastSeenProducts;
};
